// AdScreenEnhanced.js - Plays ALL active ads back-to-back, then hands off to the app
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';

function AdScreenEnhanced({ onComplete, onReady }) {
  const [phase, setPhase] = useState('fetching'); // 'fetching' | 'playing' | 'done'
  const [countdown, setCountdown] = useState(null);
  const [currentAd, setCurrentAd] = useState(null);
  const [adSettings, setAdSettings] = useState(null);
  // Tick key: incremented every time we want the countdown effect to restart.
  // This avoids the need for a 'transition' phase entirely.
  const [tickKey, setTickKey] = useState(0);

  const playlistRef = useRef([]);
  const indexRef     = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const doneRef = useRef(false);

  /* ---------------------------------------------------------- */
  /* advance — move to next ad or finish                         */
  /* ---------------------------------------------------------- */
  const advance = useCallback(() => {
    if (doneRef.current) return;

    const playlist = playlistRef.current;
    const next = indexRef.current + 1;

    if (next < playlist.length) {
      indexRef.current = next;
      const ad = playlist[next];
      const dur = ad.duration || (adSettings ? adSettings.defaultDuration : 4) || 4;
      setCurrentAd(ad);
      setCountdown(dur);
      setTickKey(k => k + 1);
    } else {
      doneRef.current = true;
      setPhase('done');
      sessionStorage.setItem('adShown', 'true');
      setTimeout(() => onCompleteRef.current(), 250);
    }
  }, [adSettings]);

  /* ---------------------------------------------------------- */
  /* 1. Fetch + build playlist                                   */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const shown = sessionStorage.getItem('adShown');

        const settingsDocs = await getDocs(query(collection(db, 'adSettings'), limit(1)));
        const settings = settingsDocs.empty
          ? { enabled: true, rotationType: 'sequential', defaultDuration: 4, skipEnabled: true, showOnce: false }
          : settingsDocs.docs[0].data();

        if (cancelled) return;
        if (!settings.enabled || (shown && settings.showOnce)) {
          onReady?.();
          onCompleteRef.current();
          return;
        }

        const snap = await getDocs(
          query(collection(db, 'advertisements'), where('active', '==', true), orderBy('order', 'asc'))
        );
        if (cancelled) return;
        if (snap.empty) { onReady?.(); onCompleteRef.current(); return; }

        let ads = snap.docs.map(d => ({ id: d.id, ...d.data() }));

        if (settings.rotationType === 'random') {
          for (let i = ads.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [ads[i], ads[j]] = [ads[j], ads[i]];
          }
        }

        playlistRef.current = ads;
        indexRef.current     = 0;

        setAdSettings(settings);
        setCurrentAd(ads[0]);
        setCountdown(ads[0].duration || settings.defaultDuration || 4);
        setPhase('playing');
        onReady?.(); // ads loaded and ready — loading screen can now hide
      } catch {
        if (!cancelled) { onReady?.(); onCompleteRef.current(); }
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  /* ---------------------------------------------------------- */
  /* 2. Countdown timer — keyed on tickKey so it restarts cleanly */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    if (phase !== 'playing' || countdown === null || countdown <= 0) {
      return;
    }

    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0; // just stop. The effect below handles what happens at 0.
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [phase, tickKey]); // tickKey changes → old interval cleaned up, new one starts

  /* ---------------------------------------------------------- */
  /* 3. "Countdown reached zero" effect — calls advance for images */
  /* ---------------------------------------------------------- */
  useEffect(() => {
    if (phase !== 'playing' || countdown !== 0) return;
    if (currentAd?.type !== 'video') {
      advance();
    }
  }, [countdown, phase, currentAd, advance]);

  /* ---------------------------------------------------------- */
  /* helpers                                                     */
  /* ---------------------------------------------------------- */
  const handleSkip  = () => { if (adSettings?.skipEnabled) advance(); };
  const handleClick = () => { if (currentAd?.link) window.open(currentAd.link, '_blank'); };

  /* ---------------------------------------------------------- */
  /* 4. Render                                                   */
  /* ---------------------------------------------------------- */
  if (phase === 'fetching' || phase === 'done') return null;

  const adIndex  = indexRef.current;
  const totalAds = playlistRef.current.length;

  return (
    <div style={styles.fullscreen}>

      {/* Counter badge */}
      {totalAds > 1 && (
        <div style={styles.counterBadge}>{adIndex + 1} / {totalAds}</div>
      )}

      {/* Media */}
      <div onClick={handleClick} style={{ ...styles.adContent, cursor: currentAd.link ? 'pointer' : 'default' }}>
        {currentAd.type === 'image' ? (
          <img src={currentAd.fileUrl} alt={currentAd.title} style={styles.media} />
        ) : (
          <video
            key={currentAd.id}   // new key = fresh mount = autoPlay fires
            autoPlay
            muted
            playsInline
            onEnded={advance}
            style={styles.media}
          >
            <source src={currentAd.fileUrl} type="video/mp4" />
          </video>
        )}
      </div>

      {/* Skip button */}
      {adSettings?.skipEnabled && (
        <button onClick={handleSkip} style={styles.skipBtn}>
          {currentAd.type === 'video' && countdown <= 0 ? 'Skip' : `Skip Ad (${countdown}s)`}
        </button>
      )}

      {/* Bottom countdown text — images only */}
      {currentAd.type === 'image' && (
        <div style={styles.countdownText}>
          Ad will close in {countdown} second{countdown !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------ */
/* Styles                                                        */
/* ------------------------------------------------------------ */
const styles = {
  fullscreen: {
    position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
    backgroundColor: '#000',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10000,
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
  },
  spinnerWrap: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner: {
    width: 40, height: 40,
    border: '3px solid rgba(255,255,255,0.15)',
    borderTop: '3px solid rgba(255,255,255,0.8)',
    borderRadius: '50%',
    animation: 'adSpin 0.7s linear infinite'
  },
  counterBadge: {
    position: 'absolute', top: 16, left: 16,
    background: 'rgba(255,255,255,0.15)',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    color: 'rgba(255,255,255,0.9)',
    fontSize: 13, fontWeight: 600,
    padding: '6px 12px', borderRadius: 20,
    border: '1px solid rgba(255,255,255,0.25)'
  },
  adContent: {
    flex: 1, display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    width: '100%', padding: '16px'
  },
  media: {
    maxWidth: '95%', maxHeight: '80vh',
    borderRadius: 12, objectFit: 'contain',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
  },
  skipBtn: {
    position: 'absolute', top: 16, right: 16,
    backgroundColor: 'rgba(255,255,255,0.15)', color: '#fff',
    border: '1.5px solid rgba(255,255,255,0.4)', borderRadius: 8,
    padding: '8px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
    backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    transition: 'background 0.2s, border-color 0.2s',
    WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation'
  },
  countdownText: {
    position: 'absolute', bottom: 28,
    left: '50%', transform: 'translateX(-50%)',
    color: 'rgba(255,255,255,0.7)',
    fontSize: 13, fontWeight: 500, whiteSpace: 'nowrap'
  }
};

if (typeof document !== 'undefined' && !document.getElementById('adSpinKeyframes')) {
  const s = document.createElement('style');
  s.id = 'adSpinKeyframes';
  s.textContent = '@keyframes adSpin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

export default AdScreenEnhanced;